import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useApi } from "shared/services/api/useApi";

export const useProjectLeadAssignment = () => {
    const { getDataAsync, postData, putData } = useApi();
    const queryClient = useQueryClient();
    const getProjectLeadAssignmentByIdApiCall = useCallback(
        async (projectId) => {
            const response = await getDataAsync("getLeadAssignmentByProject", {
                urlParams: [{ name: "$projectId", value: projectId }],
            });

            const result = response.status === 204
                ? null
                : response.json();

            return result;

        },
        [getDataAsync]
    );

    const createAssignmentApiCall = useCallback(
        (body) => {
            return postData("assignments", {
                body,
            });
        },
        [postData]
    );

    const updateAssignmentApiCall = useCallback(
        (body) => {
            return putData("assignments", {
                body,
            });
        },
        [putData]
    );

    const mutationOptions = useMemo(() => {
        return {
            onSuccess: () => {
                queryClient.invalidateQueries("leadAssignmentList");
            },
        };
    }, [queryClient]);

    const createAssignment = useMutation(
        createAssignmentApiCall,
        mutationOptions
    );

    const updateAssignment = useMutation(
        updateAssignmentApiCall,
        mutationOptions
    );

    return useMemo(() => {
        const useLeadAssignment = (projectId) =>
            useQuery(["leadAssignmentList", projectId], () =>
                getProjectLeadAssignmentByIdApiCall(projectId)
            );
        return {
            useLeadAssignment,
            createAssignment,
            updateAssignment,
        };
    }, [
        getProjectLeadAssignmentByIdApiCall,
        createAssignment,
        updateAssignment,
    ]);
};

export const useClientCampaign = () => {
    const { getDataAsync } = useApi();
    const getCampaignsByIdApiCall = useCallback(
        async (campaignId) => {
            const response = await getDataAsync("getCampaignsById", {
                urlParams: [{ name: "$campaignId", value: campaignId }],
            });

            const result = response.status === 204
                ? null
                : response.json();

            return result;

        },
        [getDataAsync]
    );

    return useMemo(() => {
        const useCampaignById = (campaignId) =>
            useQuery(["campaginById", campaignId], () =>
                getCampaignsByIdApiCall(campaignId)
            );

        return {
            useCampaignById,
        };
    }, [getCampaignsByIdApiCall]);
};

export const useClientOffice = () => {
    const { getDataAsync } = useApi();
    const getOfficeByIdApiCall = useCallback(
        async ({ officeId }) => {
            if (officeId) {
                const response = await getDataAsync("getOfficeById", {
                    urlParams: [{ name: "$id", value: officeId }],
                });

                const result = response.status === 204
                    ? null
                    : response.json();

                return result;

            } else {
                return null;
            }
        },
        [getDataAsync]
    );

    return useMemo(() => {
        const useGetClientOfficeById = (dataObj) =>
            useQuery(["getOfficeByIdApiCall", dataObj], () =>
                getOfficeByIdApiCall(dataObj)
            );

        return {
            useGetClientOfficeById,
        };
    }, [getOfficeByIdApiCall]);
};
